// src/LadingPrincipal.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "./firebase";
import { useCuerpo } from "./context/CuerpoContext";
import { companiaActiva, ordenarCompanias, prepararCompania } from "./services/companiasTenant";
import { rutaCuerpo } from "./services/rutasCuerpo";
import "./LadingPrincipal.css";

function cacheKey(cuerpoId) {
  return `bomberos_en_servicio_companias_${String(cuerpoId || "").toLowerCase()}`;
}

function leerCache(cuerpoId) {
  try {
    const raw = localStorage.getItem(cacheKey(cuerpoId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function guardarCache(cuerpoId, lista) {
  try { localStorage.setItem(cacheKey(cuerpoId), JSON.stringify(lista)); } catch (_) {}
}

const LadingPrincipal = () => {
  const navigate = useNavigate();
  const { cuerpo, cuerpoId, cuerpoSlug, cargandoCuerpo } = useCuerpo();

  const [companias, setCompanias] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [logosFallidos, setLogosFallidos] = useState({});

  useEffect(() => {
    let activo = true;

    async function cargarCompanias() {
      if (cargandoCuerpo) return;
      if (!cuerpoId) {
        if (activo) {
          setError("No se pudo determinar el Cuerpo de Bomberos.");
          setCargando(false);
        }
        return;
      }

      const cache = leerCache(cuerpoId);
      if (cache.length > 0 && activo) {
        setCompanias(cache);
        setCargando(false);
      } else {
        setCargando(true);
      }
      setError("");

      try {
        const q = query(collection(db, "companias"), where("cuerpo_id", "==", cuerpoId));
        const snap = await getDocs(q);
        const lista = snap.docs
          .map((d) => prepararCompania(d, cuerpoId))
          .filter(companiaActiva)
          .filter((c) => c.slugResuelto)
          .sort(ordenarCompanias);

        guardarCache(cuerpoId, lista);
        if (activo) setCompanias(lista);
      } catch (e) {
        console.error("Error cargando compañías del Cuerpo:", e);
        if (activo && cache.length === 0) {
          setError("No se pudieron cargar las compañías.");
          setCompanias([]);
        }
      } finally {
        if (activo) setCargando(false);
      }
    }

    cargarCompanias();
    return () => { activo = false; };
  }, [cuerpoId, cargandoCuerpo]);

  const logoCuerpo = useMemo(() => {
    const remoto = String(cuerpo?.logoURL || cuerpo?.logo_url || cuerpo?.logoUrl || cuerpo?.logo || "").trim();
    if (remoto) return remoto;
    return String(cuerpoId || "").toLowerCase() === "huasco" ? "/logos/logo-cuerpo.png" : "";
  }, [cuerpo, cuerpoId]);

  const nombreCuerpo = cuerpo?.nombre || (cuerpoId ? `Cuerpo de Bomberos ${cuerpoId}` : "Cuerpo de Bomberos");

  const handleLogoError = (id) => setLogosFallidos((prev) => ({ ...prev, [id]: true }));

  const irAdministracion = () => {
    if (cuerpoSlug) sessionStorage.setItem("superadmin_cuerpo_slug", cuerpoSlug);
    navigate("/admin-login");
  };

  return (
    <div className="landing-container" style={{ position: "relative" }}>
      <style>{`
        .landing-admin-access {
          position: absolute;
          top: 16px;
          right: 18px;
          z-index: 20;
        }

        .landing-admin-button {
          padding: 9px 16px;
          border: none;
          border-radius: 999px;
          background: #2c3e50;
          color: #fff;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 2px 7px rgba(0,0,0,0.22);
          white-space: nowrap;
        }

        @media (max-width: 768px) {
          .landing-admin-access {
            position: static;
            width: 100%;
            box-sizing: border-box;
            display: flex;
            justify-content: flex-end;
            padding: 12px 14px 0;
            margin-bottom: 14px;
          }

          .landing-admin-button {
            font-size: 15px;
            padding: 9px 15px;
          }

          .landing-title {
            margin-top: 0 !important;
          }
        }

        @media (max-width: 480px) {
          .landing-admin-access {
            justify-content: center;
            padding-top: 10px;
            margin-bottom: 16px;
          }

          .landing-admin-button {
            width: auto;
            max-width: calc(100vw - 32px);
          }
        }
      `}</style>

      <div className="landing-admin-access">
        <button
          type="button"
          onClick={irAdministracion}
          className="landing-admin-button"
        >
          Administración
        </button>
      </div>

      <h1 className="landing-title">BIENVENIDO A LA PLATAFORMA WEB DE GESTIÓN DE BOMBEROS EN SERVICIO</h1>
      <p className="landing-subtitle">Selecciona el Gallardete de tu Compañía para Ingresar</p>

      <div className="landing-cuerpo-wrapper">
        {logoCuerpo ? (
          <img src={logoCuerpo} alt={nombreCuerpo} className="landing-logo-cuerpo" />
        ) : (
          <div style={{ padding: 20, textAlign: "center", color: "#666" }}>
            <strong>{nombreCuerpo}</strong><br />
            <span>Logo institucional no configurado</span>
          </div>
        )}
      </div>

      {cargando && companias.length === 0 && <p>Cargando compañías...</p>}
      {error && <p style={{ color: "#b00020" }}>{error}</p>}

      {!cargando && !error && companias.length === 0 && (
        <p>No hay compañías activas configuradas para este Cuerpo.</p>
      )}

      <div className="landing-grid">
        {companias.map((cia) => {
          if (!cia.logoResuelto || logosFallidos[cia.id]) return null;
          return (
            <div
              key={cia.id}
              className="landing-card"
              onClick={() => navigate(rutaCuerpo(cuerpoSlug, `/cia/${cia.slugResuelto}`))}
            >
              <img
                src={cia.logoResuelto}
                alt={cia.nombre}
                className="landing-card-logo"
                onError={() => handleLogoError(cia.id)}
              />
              <p className="landing-card-title">{cia.nombre}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LadingPrincipal;
